"use client";

import { ReactNode, useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import PeeqdSidebar from "@/components/peeqd/PeeqdSidebar";
import { PEEQD_LEFT_RAIL_ENABLED } from "@/config/peeqdNav";

interface AppShellProps {
  children: ReactNode;
}

/**
 * Peeqd app shell - fixed left rail on desktop, overlay on mobile.
 * Left rail is gated by PEEQD_LEFT_RAIL_ENABLED for pre-launch release.
 */
export default function AppShell({ children }: AppShellProps) {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!PEEQD_LEFT_RAIL_ENABLED) return;
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  useEffect(() => {
    if (!PEEQD_LEFT_RAIL_ENABLED) return;
    (window as Window & { __toggleMobileSidebar?: () => void }).__toggleMobileSidebar = () =>
      setMobileOpen(true);
    return () => {
      delete (window as Window & { __toggleMobileSidebar?: () => void }).__toggleMobileSidebar;
    };
  }, []);

  if (pathname?.startsWith("/auth/popup")) {
    return <>{children}</>;
  }

  const isStandaloneRoute =
    pathname?.startsWith("/admin_tools") ||
    pathname?.startsWith("/editor") ||
    pathname?.startsWith("/auth/") ||
    pathname?.startsWith("/track");

  if (isStandaloneRoute) {
    return <>{children}</>;
  }

  if (!PEEQD_LEFT_RAIL_ENABLED) {
    return <div className="min-h-screen bg-bg">{children}</div>;
  }

  return (
    <>
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden
        />
      )}

      <div className="fixed inset-y-0 left-0 z-50 hidden lg:block">
        <PeeqdSidebar />
      </div>

      <div
        className={`fixed inset-y-0 left-0 z-50 transition-transform duration-300 lg:hidden ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <PeeqdSidebar onClose={() => setMobileOpen(false)} />
      </div>

      <div className="min-h-screen bg-bg lg:pl-60">{children}</div>
    </>
  );
}
